import { z } from 'zod';
import { PolicyCategorySchema, type PolicySnapshot } from './organization-policy';
import { AssessedRiskLevelSchema } from './risk';

export const ResearchCategorySchema = PolicyCategorySchema;
export const CategoryCoverageSchema = z.object({
  category: ResearchCategorySchema,
  status: z.enum([
    'researched',
    'no_findings',
    'insufficient_evidence',
    'inaccessible',
    'failed',
    'not_screened',
  ]),
  sourceIds: z.array(z.string()),
  successfulSearches: z.number().optional(),
  failedSearches: z.number().optional(),
  unavailableSources: z.number().optional(),
  explanation: z.string(),
});
export const FactorContributionSchema = z.object({
  category: ResearchCategorySchema,
  units: z.number(),
  percentage: z.number(),
});
export const ResearchSubjectSchema = z.object({
  kind: z.enum(['individual', 'company', 'member']),
  id: z.string(),
  name: z.string().min(1),
  context: z.record(z.string(), z.string()),
  confirmedIdentityEvidence: z
    .array(
      z.object({
        reportId: z.string(),
        sourceUrl: z.string(),
        excerpt: z.string(),
        details: z.string(),
      }),
    )
    .optional(),
  submittedEvidence: z.array(z.object({ id: z.string(), text: z.string() })).optional(),
});
export type ResearchSubject = z.infer<typeof ResearchSubjectSchema>;

export const ResearchConfigSchema = z.object({
  maxPages: z.coerce.number().int().min(1).max(100).default(20),
  maxQueries: z.coerce.number().int().min(1).max(100).default(18),
  concurrency: z.coerce.number().int().min(1).max(10).default(3),
  timeoutMs: z.coerce.number().int().min(1000).max(120000).default(45000),
  maxResponseBytes: z.coerce.number().int().min(1000).max(5000000).default(1000000),
  maxPageCharacters: z.coerce.number().int().min(1000).max(100000).default(30000),
  retries: z.coerce.number().int().min(0).max(3).default(2),
  model: z.string().default('gemini-flash-latest'),
  provider: z.enum(['google', 'openrouter']).optional(),
});
export type ResearchConfig = z.infer<typeof ResearchConfigSchema>;

export const SearchResultSchema = z.object({
  title: z.string(),
  url: z.url(),
  snippet: z.string(),
  rank: z.number(),
  category: ResearchCategorySchema.optional(),
});
export type SearchResult = z.infer<typeof SearchResultSchema>;

export const IdentityAttributeSchema = z.object({
  field: z.enum([
    'name',
    'dateOfBirth',
    'placeOfBirth',
    'nationality',
    'residenceCountry',
    'companyRegistrationNumber',
    'employer',
    'profileUrl',
  ]),
  value: z.string().min(1),
  excerpt: z.string().min(1),
});
export const SocialActivitySchema = z.object({
  platform: z.string(),
  account: z.string(),
  author: z.string(),
  kind: z.enum(['profile', 'post', 'article', 'comment', 'repost', 'mention', 'reaction']),
  permalink: z.string(),
  publishedAt: z.string().nullable(),
});
export const EvidenceAnalysisSchema = z.object({
  match: z.enum(['matching', 'uncertain', 'unrelated']),
  matchReason: z.string().min(1),
  identityAttributes: z.array(IdentityAttributeSchema).optional(),
  coveredCategories: z.array(ResearchCategorySchema).optional(),
  aliases: z.array(z.string().min(2).max(100)).max(3).optional(),
  findings: z
    .array(
      z.object({
        type: z.enum([
          'SANCTIONS',
          'PEP_SCREENING',
          'ADVERSE_MEDIA',
          'JURISDICTION_RISK',
          'SOCIAL',
        ]),
        classification: z.enum(['INFORMATIONAL', 'DISCREPANCY', 'ADVERSE']),
        activity: SocialActivitySchema.optional(),
        eventKey: z.string().optional(),
        eventContext: z.string().optional(),
        title: z.string().min(1).max(250),
        details: z.string().min(1).max(4000),
        seriousFinancialCrime: z.boolean(),
        excerpt: z.string().min(1).max(2000),
      }),
    )
    .max(20),
});
export type EvidenceAnalysis = z.infer<typeof EvidenceAnalysisSchema>;

export const AssessmentSchema = z.object({
  profileSummary: z.preprocess((value) => value ?? undefined, z.string().optional()),
  riskLevel: AssessedRiskLevelSchema,
  summary: z.string(),
  provisional: z.boolean(),
  reviewComplete: z.boolean().optional(),
  coverageComplete: z.boolean(),
  requiredChecksComplete: z.boolean().optional(),
  acceptanceBlockers: z.array(z.string()).optional(),
  reasons: z
    .array(
      z.object({
        subjectId: z.string(),
        findingKey: z.string(),
        title: z.string(),
        category: z.string(),
        severity: z.string(),
        resolution: z.string(),
      }),
    )
    .optional(),
  reviewedRiskLevel: AssessedRiskLevelSchema.optional(),
  factors: z.array(FactorContributionSchema).optional(),
  reviewedFactors: z.array(FactorContributionSchema).optional(),
  subjects: z.array(
    z.object({
      subjectId: z.string(),
      riskLevel: AssessedRiskLevelSchema,
      coverageComplete: z.boolean(),
      reviewedRiskLevel: AssessedRiskLevelSchema.optional(),
    }),
  ),
});
export type Assessment = z.infer<typeof AssessmentSchema>;

export const GenerationStatusSchema = z.enum(['PROCESSING', 'PARTIAL', 'FINISHED', 'FAILED']);
export type GenerationStatus = z.infer<typeof GenerationStatusSchema>;
export const SubjectProgressSchema = z.object({
  subjectId: z.string(),
  stage: z.enum([
    'pending',
    'searching',
    'reading',
    'resolving_identity',
    'following_up',
    'screening',
    'analyzing',
    'assembling',
    'completed',
  ]),
  execution: z.enum(['completed', 'partial', 'failed']).optional(),
  successfulSearches: z.number().optional(),
  coverage: z.enum([
    'pending',
    'complete',
    'no_results',
    'no_relevant_evidence',
    'limited',
    'inaccessible',
    'provider_failure',
  ]),
  errors: z.array(z.string()),
  summary: z.string().default(''),
  categories: z.array(CategoryCoverageSchema).optional(),
  successfulAnalyses: z.number().optional(),
  rejectedFindings: z.number().optional(),
  diagnostics: z
    .array(
      z.object({
        stage: z.string(),
        code: z.string(),
        // Older Mixed Mongo records serialized absent optional fields as BSON null.
        statusCode: z.preprocess((value) => value ?? undefined, z.number().optional()),
        providerCode: z.preprocess((value) => value ?? undefined, z.string().optional()),
        retryable: z.boolean(),
        attempts: z.number(),
        validationReason: z.preprocess((value) => value ?? undefined, z.string().optional()),
        sourceUrl: z.preprocess((value) => value ?? undefined, z.string().optional()),
      }),
    )
    .optional(),
  budget: z
    .object({
      queries: z.number(),
      pages: z.number(),
      activeMs: z.number(),
      expanded: z.boolean(),
      exhausted: z.boolean(),
    })
    .optional(),
  platforms: z
    .array(
      z.object({
        platform: z.string(),
        profiles: z.number(),
        activities: z.number(),
        status: z.enum([
          'researched',
          'not_retrieved',
          'no_results',
          'inaccessible',
          'not_searched',
          'identity_unresolved',
        ]),
        profileStatus: z
          .enum([
            'found',
            'no_results',
            'inaccessible',
            'not_searched',
            'identity_unresolved',
            'not_retrieved',
          ])
          .optional(),
        activityStatus: z
          .enum([
            'retrieved',
            'no_results',
            'inaccessible',
            'not_searched',
            'identity_unresolved',
            'not_retrieved',
          ])
          .optional(),
        explanation: z.string(),
      }),
    )
    .optional(),
});
export type SubjectProgress = z.infer<typeof SubjectProgressSchema>;

export type Generation = {
  _id: string;
  organizationId: string;
  reportId: string;
  input: unknown;
  subjects: ResearchSubject[];
  config: ResearchConfig;
  policySnapshot?: Omit<PolicySnapshot, 'version'>;
  status: GenerationStatus;
  progress: SubjectProgress[];
  checkpoints: Record<string, unknown>;
  events: { id: string; at: string; stage: string; message: string; subjectId?: string }[];
  usage: {
    searches: number;
    reads: number;
    analyses: number;
    inputTokens: number;
    outputTokens: number;
  };
  leaseToken?: string | null;
  leaseUntil: Date;
  recoveryAttempts: number;
  createdAt: Date;
  updatedAt: Date;
};

export type ResearchEvidence = {
  _id: string;
  organizationId: string;
  reportId: string;
  generationId: string;
  url: string;
  title: string;
  text: string;
  contentHash: string;
  retrievedAt: Date;
  status: 'read' | 'blocked' | 'empty' | 'failed';
};
